import { DecimalPipe } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { HeaderComponent } from "@components/header/header.component";
import { DivListComponent } from "@components/list/div-list/div-list.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { Subject, combineLatest, iif } from "rxjs";
import { filter, map, switchMap, takeUntil } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VardeberakningRequestDTO } from "~app/features/virkeskop/dto/vardeberakning/vardeberakning-request-dto";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { SortimentSnittnetto } from "~app/features/virkeskop/models/vardeberakning/vardeberakning-models";
import { AvverkningskostnaderMediatorService } from "~app/features/virkeskop/services/avverkningskostnader-mediator.service";
import { ForkalkylService } from "~app/features/virkeskop/services/forkalkyl.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VirkeskostnaderMediatorService } from "../../../services/virkeskostnader-mediator.service";

@Component({
    selector: "rask-forkalkyl-nettoberakning",
    templateUrl: "./forkalkyl-nettoberakning.component.html",
    styleUrls: ["./forkalkyl-nettoberakning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        DivListComponent,
        ListColumnDirective,
        DecimalPipe
    ]
})
export class ForkalkylNettoberakningComponent implements OnInit, OnDestroy {
    @Input() atgardGuid: string;
    kontrakt: Kontrakt;
    sortimentSnittnetto: SortimentSnittnetto;

    private readonly avverkningskostnaderMediatorService = inject(AvverkningskostnaderMediatorService);
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.kontrakt = this.kontraktMediatorService.kontrakt;

        const vardeberakningDefault$ = this.kontraktMediatorService.kontrakt$.pipe(
            filter(kontrakt => ObjectUtils.hasValue(kontrakt.id)),
            switchMap(kontrakt => this.forkalkylService.vardeberakningDefault(kontrakt.id)),
            map(vardeberakning => vardeberakning?.length > 0 ? vardeberakning[0] : null)
        );

        combineLatest([
            this.virkeskostnaderMediatorService.virkeskostnader$,
            this.avverkningskostnaderMediatorService.avverkningskostnader$
        ]).pipe(
            takeUntil(this.ngUnsubscribe$),
            filter(([vk, ak]) => !ObjectUtils.isNullOrUndefined(vk, ak)),
            map(([vk, ak]) => this.forkalkylService.createVardeberakningRequest(this.kontrakt, vk.virkeskostnader, ak, this.atgardGuid)),
            switchMap(req => iif(
                () => this.useDefaultVardeberakning(req),
                vardeberakningDefault$,
                this.forkalkylService.vardeberakningForkalkyl(req)))
        ).subscribe((vardeberakning) => {
            this.sortimentSnittnetto = vardeberakning?.sortimentSnittnetto;
        });
    }

    rotvardeTitle(): string {
        return this.kontrakt?.isLeveransvirke ? "Rotvärde" : "Grundpris";
    }

    useDefaultVardeberakning(request: VardeberakningRequestDTO) {
        return request.virkeskopsatgarder.every(atg => atg.virkeskostnader.length === 0);
    }
}
